"use client";

import { c as _c } from "react/compiler-runtime";
import type { ReactNode } from "react";
import { LockClosedIcon, ChevronRightIcon } from "@heroicons/react/24/solid";
import { ChatBubbleLeftRightIcon } from "@heroicons/react/24/outline";
import type { ApiWave } from "@/generated/models/ApiWave";
import { getTimeAgoShort, numberWithCommas } from "@/helpers/Helpers";
import { getScaledImageUri, ImageScale } from "@/helpers/image.helpers";
import { getWaveRoute } from "@/helpers/navigation.helpers";
import Link from "next/link";
import Image from "next/image";
import WavesIcon from "@/components/common/icons/WavesIcon";
type BrainSidebarWaveItemDisplay = {
  readonly href: string;
  readonly isPrivate: boolean;
  readonly isDirectMessage: boolean;
  readonly dropsCount: string;
  readonly lastDropTimestamp: ApiWave["metrics"]["latest_drop_timestamp"];
  readonly imageSrc: string | null;
};
const getBrainSidebarWaveItemDisplay = (wave: ApiWave): BrainSidebarWaveItemDisplay => ({
  isDirectMessage: wave.chat.scope.group?.is_direct_message ?? false,
  href: getWaveRoute({
    waveId: wave.id,
    isDirectMessage: wave.chat.scope.group?.is_direct_message ?? false,
    isApp: false
  }),
  isPrivate: Boolean(wave.visibility.scope.group) && !(wave.chat.scope.group?.is_direct_message ?? false),
  dropsCount: numberWithCommas(wave.metrics.drops_count),
  lastDropTimestamp: wave.metrics.latest_drop_timestamp,
  imageSrc: wave.picture ? getScaledImageUri(wave.picture, ImageScale.W_200_H_200) : null
});
export default function UserPageBrainSidebarWaveItem(t0) {
  const $ = _c(34);
  const {
    wave
  } = t0;
  let FallbackIcon;
  let href;
  let imageSrc;
  let isPrivate;
  let metaContent;
  if ($[0] !== wave) {
    const {
      href: t1,
      isPrivate: t2,
      isDirectMessage,
      dropsCount,
      lastDropTimestamp,
      imageSrc: t3
    } = getBrainSidebarWaveItemDisplay(wave);
    href = t1;
    isPrivate = t2;
    imageSrc = t3;
    FallbackIcon = isDirectMessage ? ChatBubbleLeftRightIcon : WavesIcon;
    let t4;
    if ($[6] === Symbol.for("react.memo_cache_sentinel")) {
      t4 = <span>No drops yet</span>;
      $[6] = t4;
    } else {
      t4 = $[6];
    }
    metaContent = t4;
    if (lastDropTimestamp) {
      const t5 = <span>{getTimeAgoShort(lastDropTimestamp)}</span>;
      let t6;
      if ($[7] === Symbol.for("react.memo_cache_sentinel")) {
        t6 = <span className="tw-h-0.5 tw-w-0.5 tw-rounded-full tw-bg-white/30" />;
        $[7] = t6;
      } else {
        t6 = $[7];
      }
      let t7;
      if ($[8] !== dropsCount) {
        t7 = <span>{dropsCount} drops</span>;
        $[8] = dropsCount;
        $[9] = t7;
      } else {
        t7 = $[9];
      }
      let t8;
      if ($[10] !== t5 || $[11] !== t7) {
        t8 = <>{t5}{t6}{t7}</>;
        $[10] = t5;
        $[11] = t7;
        $[12] = t8;
      } else {
        t8 = $[12];
      }
      metaContent = t8;
    }
    $[0] = wave;
    $[1] = FallbackIcon;
    $[2] = href;
    $[3] = imageSrc;
    $[4] = isPrivate;
    $[5] = metaContent;
  } else {
    FallbackIcon = $[1];
    href = $[2];
    imageSrc = $[3];
    isPrivate = $[4];
    metaContent = $[5];
  }
  let t1;
  if ($[13] !== FallbackIcon || $[14] !== imageSrc || $[15] !== wave.name) {
    t1 = <div className="tw-relative tw-h-10 tw-w-10 tw-shrink-0 tw-overflow-hidden tw-rounded-full tw-border tw-border-solid tw-border-white/[0.04] tw-bg-iron-900 tw-shadow-sm tw-transition-colors desktop-hover:group-hover:tw-border-white/[0.1]">{imageSrc ? <Image src={imageSrc} alt={wave.name ? `Wave ${wave.name}` : "Wave picture"} fill={true} sizes="40px" className="tw-object-cover" /> : <div className="tw-flex tw-h-full tw-w-full tw-items-center tw-justify-center tw-bg-iron-900"><FallbackIcon className="tw-h-4 tw-w-4 tw-flex-shrink-0 tw-text-iron-300" /></div>}</div>;
    $[13] = FallbackIcon;
    $[14] = imageSrc;
    $[15] = wave.name;
    $[16] = t1;
  } else {
    t1 = $[16];
  }
  let t2;
  if ($[17] !== isPrivate) {
    t2 = isPrivate && <LockClosedIcon className="tw-h-3 tw-w-3 tw-shrink-0 tw-text-white/30" />;
    $[17] = isPrivate;
    $[18] = t2;
  } else {
    t2 = $[18];
  }
  let t3;
  if ($[19] !== wave.name) {
    t3 = <span className="tw-truncate tw-text-sm tw-font-semibold tw-text-iron-100 tw-transition-colors desktop-hover:group-hover:tw-text-iron-50">{wave.name}</span>;
    $[19] = wave.name;
    $[20] = t3;
  } else {
    t3 = $[20];
  }
  let t4;
  if ($[21] !== t2 || $[22] !== t3) {
    t4 = <div className="tw-mb-1 tw-flex tw-items-center tw-justify-between"><div className="tw-flex tw-min-w-0 tw-items-center tw-gap-1.5">{t2}{t3}</div></div>;
    $[21] = t2;
    $[22] = t3;
    $[23] = t4;
  } else {
    t4 = $[23];
  }
  let t5;
  if ($[24] !== metaContent) {
    t5 = <div className="tw-flex tw-items-center tw-gap-2 tw-text-xs tw-font-medium tw-text-iron-500">{metaContent}</div>;
    $[24] = metaContent;
    $[25] = t5;
  } else {
    t5 = $[25];
  }
  let t6;
  if ($[26] !== t4 || $[27] !== t5) {
    t6 = <div className="tw-flex tw-min-w-0 tw-flex-1 tw-flex-col tw-justify-center">{t4}{t5}</div>;
    $[26] = t4;
    $[27] = t5;
    $[28] = t6;
  } else {
    t6 = $[28];
  }
  let t7;
  if ($[29] === Symbol.for("react.memo_cache_sentinel")) {
    t7 = <ChevronRightIcon className="tw-h-4 tw-w-4 tw-shrink-0 -tw-translate-x-1 tw-text-iron-600 tw-opacity-0 tw-transition-all tw-duration-300 desktop-hover:group-hover:tw-translate-x-0 desktop-hover:group-hover:tw-text-iron-400 desktop-hover:group-hover:tw-opacity-100" />;
    $[29] = t7;
  } else {
    t7 = $[29];
  }
  let t8;
  if ($[30] !== href || $[31] !== t1 || $[32] !== t6) {
    t8 = <Link href={href} prefetch={false} className="tw-group tw-flex tw-cursor-pointer tw-items-center tw-gap-3 tw-rounded-xl tw-border tw-border-solid tw-border-white/10 tw-bg-iron-950/80 tw-p-3 tw-no-underline tw-shadow-2xl tw-transition-all desktop-hover:hover:tw-border-white/15">{t1}{t6}{t7}</Link>;
    $[30] = href;
    $[31] = t1;
    $[32] = t6;
    $[33] = t8;
  } else {
    t8 = $[33];
  }
  return t8;
}